// Headless Google Places (New) client (BRK-S067) — the Broker-Portal half of
// the cross-app UX contract in pms-local docs/address-autocomplete.md. We do NOT
// embed Google's prebuilt widget: this small typed client drives the
// Autocomplete (New) + Place Details REST calls, and AddressFieldset renders the
// suggestions in our own dropdown (matching BRK's typeahead idiom).
//
// Billing is per SESSION, tied together by a session token: one token spans
// every Autocomplete request of a focus-session and is consumed by the
// terminating Place Details request. `PlacesSession` owns exactly one token;
// the component mints a fresh session after each selection.
//
// The key is read in ONE place (src/config.ts, NN #6) and passed in here — a
// referrer-restricted PUBLIC browser key, not a secret (NN #5).
import {
  placesBrowserKey,
  placesAutocompleteEnabled,
  hasDevSession,
} from "@/config";
import { normalizeCounty, type AddressValue } from "@/lib/address";

const AUTOCOMPLETE_URL = "https://places.googleapis.com/v1/places:autocomplete";
const DETAILS_BASE = "https://places.googleapis.com/v1/places/";

/** Minimum characters before any suggestion request fires (shared contract). */
export const MIN_QUERY_LENGTH = 3;
/** Debounce after the last keystroke before a suggestion request (shared). */
export const DEBOUNCE_MS = 300;

export interface PlaceSuggestion {
  placeId: string;
  /** The bold first line (structuredFormat.mainText) — usually the street. */
  primaryText: string;
  /** The muted second line (structuredFormat.secondaryText) — city/state. */
  secondaryText: string;
}

export interface PlacesClient {
  autocomplete(
    input: string,
    sessionToken: string,
    signal?: AbortSignal,
  ): Promise<PlaceSuggestion[]>;
  details(placeId: string, sessionToken: string): Promise<AddressValue>;
}

// Partial shapes of the Places API (New) JSON we consume.
interface RawPlacePrediction {
  placeId?: string;
  text?: { text?: string };
  structuredFormat?: {
    mainText?: { text?: string };
    secondaryText?: { text?: string };
  };
}
interface RawAutocompleteResponse {
  suggestions?: { placePrediction?: RawPlacePrediction }[];
}
interface RawAddressComponent {
  types?: string[];
  longText?: string;
  shortText?: string;
}
interface RawDetailsResponse {
  addressComponents?: RawAddressComponent[];
}

/**
 * Flatten the Places API (New) addressComponents into our {street, city, state,
 * zip, county} — with the fallback chains the shared contract spells out:
 *  - street: street_number + route (either may be absent)
 *  - city:   locality → postal_town → sublocality_level_1 → sublocality
 *            (NYC boroughs surface as sublocality with no locality)
 *  - state:  administrative_area_level_1 SHORT text (2-letter USPS)
 *  - zip:    postal_code, trimmed to the 5-digit base (ZIP+4 dropped)
 *  - county: administrative_area_level_2, bare as PMS stores it (BRK-S308);
 *            absent when Places has none (some independent cities)
 *  - line 2: subpremise, the unit or suite (BRK-S302); absent when none
 */
export function mapAddressComponents(
  components: RawAddressComponent[],
): AddressValue {
  const pick = (type: string) =>
    components.find((c) => c.types?.includes(type));
  const long = (type: string) => pick(type)?.longText?.trim() ?? "";

  const street = [long("street_number"), long("route")]
    .filter(Boolean)
    .join(" ");
  const city =
    long("locality") ||
    long("postal_town") ||
    long("sublocality_level_1") ||
    long("sublocality");
  const state = pick("administrative_area_level_1")?.shortText?.trim() ?? "";
  const zip = long("postal_code").slice(0, 5);
  const county = normalizeCounty(long("administrative_area_level_2"));
  const addressLine2 = long("subpremise");

  return {
    street,
    ...(addressLine2 ? { addressLine2 } : {}),
    city,
    state,
    zip,
    ...(county ? { county } : {}),
  };
}

/** Mint a session token. `crypto.randomUUID` in every target browser + jsdom; a
 *  cheap fallback keeps it defined if the platform lacks it. */
export function newSessionToken(): string {
  try {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through to the fallback */
  }
  return `sess-${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
}

/**
 * One Google Places billing session — holds exactly one token. Every
 * `suggest()` reuses it; `finalize()` consumes it on the Place Details call.
 * Discard the instance after `finalize()` and mint a new one for the next
 * focus-session (that's how a fresh token is minted per the contract).
 */
export class PlacesSession {
  readonly token: string;
  constructor(token: string = newSessionToken()) {
    this.token = token;
  }
  suggest(
    client: PlacesClient,
    input: string,
    signal?: AbortSignal,
  ): Promise<PlaceSuggestion[]> {
    return client.autocomplete(input, this.token, signal);
  }
  finalize(client: PlacesClient, placeId: string): Promise<AddressValue> {
    return client.details(placeId, this.token);
  }
}

/**
 * A fetch-based Places client. The browser key (when present) rides in the
 * `X-Goog-Api-Key` header; under the dev-mock path the key is null and the dev
 * API stub answers these URLs from fixtures (the header is ignored there).
 */
export function createHttpPlacesClient(apiKey: string | null): PlacesClient {
  const keyHeader: Record<string, string> = apiKey
    ? { "X-Goog-Api-Key": apiKey }
    : {};
  return {
    async autocomplete(input, sessionToken, signal) {
      const res = await fetch(AUTOCOMPLETE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...keyHeader },
        body: JSON.stringify({
          input,
          sessionToken,
          includedRegionCodes: ["us"],
        }),
        signal,
      });
      if (!res.ok) throw new Error(`places autocomplete failed: ${res.status}`);
      const data = (await res.json()) as RawAutocompleteResponse;
      return (data.suggestions ?? [])
        .map((s) => s.placePrediction)
        .filter((p): p is RawPlacePrediction => Boolean(p?.placeId))
        .map((p) => ({
          placeId: p.placeId as string,
          primaryText: p.structuredFormat?.mainText?.text ?? p.text?.text ?? "",
          secondaryText: p.structuredFormat?.secondaryText?.text ?? "",
        }));
    },
    async details(placeId, sessionToken) {
      const url =
        `${DETAILS_BASE}${encodeURIComponent(placeId)}` +
        `?sessionToken=${encodeURIComponent(sessionToken)}`;
      const res = await fetch(url, {
        headers: { "X-Goog-FieldMask": "addressComponents", ...keyHeader },
      });
      if (!res.ok) throw new Error(`places details failed: ${res.status}`);
      const data = (await res.json()) as RawDetailsResponse;
      return mapAddressComponents(data.addressComponents ?? []);
    },
  };
}

/**
 * The client AddressFieldset uses by default, or null when autocomplete is off
 * (no key + no dev session) — in which case the fieldset is the plain manual
 * form. Enablement + key both resolve through src/config.ts (NN #6).
 */
export function defaultPlacesClient(): PlacesClient | null {
  const key = placesBrowserKey();
  if (!placesAutocompleteEnabled(key, hasDevSession())) return null;
  return createHttpPlacesClient(key);
}
